/**
 * Venus wood range hoods (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 2): wood hoods hung over a Venus run's range, in the run's finish,
 * each with a hood insert (the fan, filters and lights) bought with it —
 * `WRH`, the curved hood Venus sells today and the one offered first (the
 * user, 2026-11-01), and `RHA`, the 2024 catalog's box hood, where the list
 * prices it. Venus runs only (the user, 2026-10-31: "for the other lines, we
 * just dont offer a wood hood").
 *
 * The hood stays a `vent-hood` object; the wood build is its `hoodSku`
 * (`fixture-types/hoodForm.isWoodHood`). This module is the rule: which sizes
 * a line sells, the panel's tiles and why one is closed, the insert each
 * width takes, and the look a hood takes from its run. Pure.
 */
const { unitsOf } = require('./vendorSizes');
const { isWoodHood } = require('../fixture-types/hoodForm');

const IN = 0.0254;
const TILE_PREFIX = 'procedural-vent-hood-wood-';

/**
 * The wood hood styles, by the SKU's letters, in the order the panel offers
 * them. `depthIn` is the hood's own (a molding may stand proud of it).
 */
const WOOD_HOOD_STYLES = Object.freeze({
  // "HF-WRH3036 … Wood Wall Mounted Range Hoods" (the store, 2026-11-01):
  // curved sides flaring from a 12"-deep chimney down to a 9" base band
  WRH: Object.freeze({ shape: 'curved', depthIn: 19.5, label: 'Curved' }),
  // "Wall - Wood Range Hood" (2024 catalog p.18): a box, an arched apron
  RHA: Object.freeze({ shape: 'box', depthIn: 22, label: 'Box' }),
});
const STYLE_ORDER = Object.keys(WOOD_HOOD_STYLES);
const SKU_RE = /^(RHA|WRH)(\d{2})(\d{2})$/;

/** `{ style, w, h }` (inches) a wood hood SKU names, or null. */
function woodHoodSkuOf(sku) {
  const m = SKU_RE.exec(String(sku || ''));
  return m ? { style: m[1], w: +m[2], h: +m[3] } : null;
}

/** A wood hood's shape — `'curved'` (WRH) or `'box'` (RHA) — or null. */
function woodHoodShapeOf(obj) {
  if (!isWoodHood(obj)) return null;
  return WOOD_HOOD_STYLES[woodHoodSkuOf(obj.hoodSku)?.style]?.shape ?? null;
}

/**
 * The curved hood's parts, the same at every size (the store's drawings of
 * all five, 2026-11-01): a chimney 9" narrower than the hood and 12" deep,
 * straight for its top 6"; the sides and front flaring out from it to the
 * hood's width and depth over the rest; a 9" base band under the flare, a
 * cap molding over the band and a base molding at the bottom, each standing
 * proud of the band's front and sides.
 */
const CURVED_WOOD_HOOD = Object.freeze({
  chimneyNarrowerIn: 9,
  chimneyDepthIn: 12,
  chimneyStraightIn: 6,
  bandIn: 9,
  capMolding: Object.freeze({ heightIn: 1, proudIn: 0.75 }),
  baseMolding: Object.freeze({ heightIn: 1.5, proudIn: 1 }),
});

/**
 * How far the box hood's ledge stands proud of its front and sides (2024
 * catalog p.16) — the one part of it outside its box.
 */
const BOX_HOOD_LEDGE_PROUD_IN = 1.25;

/**
 * How far a wood hood's moldings stand proud of its sides (metres): the
 * curved hood's base molding, the box hood's ledge; null for any other hood.
 * The uppers beside it stand that far clear, a filler between
 * (`applianceInstall.hoodSkySpans`, `hoodClearance.js`).
 */
function woodHoodSideProudM(hood) {
  const shape = woodHoodShapeOf(hood);
  if (shape === 'curved') {
    const { capMolding, baseMolding } = CURVED_WOOD_HOOD;
    return Math.max(capMolding.proudIn, baseMolding.proudIn) * IN;
  }
  if (shape === 'box') return BOX_HOOD_LEDGE_PROUD_IN * IN;
  return null;
}

/**
 * The box hood's apron — the arched front below the ledge — as a share of
 * its height, kept between 6" and 10" and to no more than half a short hood.
 */
const BOX_HOOD_APRON = Object.freeze({
  frac: 0.3,
  minIn: 6,
  maxIn: 10,
  maxFrac: 0.5,
});

/** The box hood's apron height for a hood `heightM` tall (metres). */
function boxHoodApronM(heightM) {
  const a = BOX_HOOD_APRON;
  return Math.min(
    Math.max(heightM * a.frac, a.minIn * IN),
    a.maxIn * IN,
    heightM * a.maxFrac
  );
}

/**
 * How far above a wood hood's bottom its insert sits (metres) — on the box
 * hood's ledge, on top of the curved hood's base band. The front drops below
 * the insert to hide it, but the back does not: the hood is open against the
 * wall up to the insert, so the backsplash runs up to here (the user,
 * 2026-11-01: "the wood box doesnt come all the way down on the backside").
 * Null for any other hood.
 */
function woodHoodInsertRiseM(hood) {
  const shape = woodHoodShapeOf(hood);
  if (shape === 'curved') return CURVED_WOOD_HOOD.bandIn * IN;
  if (shape === 'box') return boxHoodApronM(hood.dimensions?.y ?? 0);
  return null;
}

/**
 * The hood insert each width of wood hood takes, and our cost for one before
 * the materials markup ("Default estimate"), from ZLINE's 695 series retail
 * (Oct 2026: 28" $672 Walmart / $774 Ferguson; 34" $649 eBay / $786 Ferguson;
 * 40" $850–968 Ferguson) — the design's "Figures to check (group 2)".
 */
const HOOD_INSERTS = Object.freeze({
  30: Object.freeze({ widthIn: 28, costEach: 675 }),
  36: Object.freeze({ widthIn: 34, costEach: 700 }),
  42: Object.freeze({ widthIn: 40, costEach: 850 }),
  // ZLINE 695-46 (Nov 2026: $864 sale / $960 list, Best Buy)
  48: Object.freeze({ widthIn: 46, costEach: 950 }),
});

const styleRank = (sku) => STYLE_ORDER.indexOf(woodHoodSkuOf(sku)?.style);

/**
 * The wood hoods a line sells, `{ sku, w, h, d, colours }` (inches), curved
 * first then by size; `[]` off Venus.
 */
function woodHoodUnits(family) {
  return unitsOf(family, 'wood-hood')
    .map(({ sku, w, h, d, colours }) => ({
      sku,
      w,
      h,
      d,
      colours: [...(colours || [])],
    }))
    .sort(
      (a, b) => styleRank(a.sku) - styleRank(b.sku) || a.w - b.w || a.h - b.h
    );
}

const colourName = (family, code) =>
  (family?.offering?.paint || []).find((p) => p.code === code)?.name || code;

const sizeOf = (u) => ({ x: u.w * IN, y: u.h * IN, z: u.d * IN });

/** The panel tile id a wood hood of this SKU is picked by. */
const woodHoodTileId = (sku) => `${TILE_PREFIX}${String(sku).toLowerCase()}`;

const feetInches = (inches) => {
  const all = Math.round(inches);
  return `${Math.floor(all / 12)}' ${all % 12}"`;
};

/** Why this size cannot go over this range in this colour, or null. */
function closedReason(
  family,
  unit,
  { colourCode, rangeWidthIn, roomAboveIn = null },
  widths
) {
  if (!Number.isFinite(rangeWidthIn))
    return 'There is no range under this hood';
  if (!colourCode)
    return `These cabinets wear a finish ${family.name} doesn't sell, so it has no wood hood`;
  if (!unit.colours.includes(colourCode))
    return `${family.name} doesn't sell ${unit.sku} in ${colourName(family, colourCode)}`;
  if (!widths.has(Math.round(rangeWidthIn)))
    return `${family.name} makes no wood hood for a ${Math.round(rangeWidthIn)}" range`;
  if (Math.round(rangeWidthIn) !== unit.w)
    return `This range is ${Math.round(rangeWidthIn)}" wide; a wood hood over it is ${Math.round(rangeWidthIn)}"`;
  if (Number.isFinite(roomAboveIn) && unit.h > roomAboveIn + 0.5)
    return `${unit.sku} is ${unit.h}" tall; there is ${feetInches(roomAboveIn)} from the hood's bottom to the ceiling`;
  return null;
}

/**
 * The hood panel's wood tiles for a hood on a run of `family`, curved first,
 * each closed with its reason where it cannot go: a colour the line does not
 * price it in first, then a range width no wood hood comes in, then a width
 * other than the range's, then a hood too tall for the room over its bottom.
 * `[]` off Venus.
 *
 * @param {Object} family
 * @param {{ colourCode: string|null, rangeWidthIn: number|null,
 *   roomAboveIn?: number|null }} at  `rangeWidthIn` null: no range under the
 *   hood (every size closed); `roomAboveIn` the height from where the hood's
 *   bottom hangs to the ceiling, null when unknown
 */
function woodHoodTiles(family, at) {
  const units = woodHoodUnits(family);
  const widths = new Set(units.map((u) => u.w));
  return units.map((u) => ({
    id: woodHoodTileId(u.sku),
    sku: u.sku,
    name: `${WOOD_HOOD_STYLES[woodHoodSkuOf(u.sku).style].label} wood hood ${u.w}" × ${u.h}" (${u.sku})`,
    widthIn: u.w,
    heightIn: u.h,
    dimensionsMeters: sizeOf(u),
    disabledReason: closedReason(family, u, at, widths),
  }));
}

/** The SKU and size a wood tile id names, or null for any other tile. */
function woodHoodTileOf(tileId) {
  if (typeof tileId !== 'string' || !tileId.startsWith(TILE_PREFIX))
    return null;
  const sku = tileId.slice(TILE_PREFIX.length).toUpperCase();
  const at = woodHoodSkuOf(sku);
  if (!at) return null;
  const d = WOOD_HOOD_STYLES[at.style].depthIn;
  return { sku, dimensionsMeters: sizeOf({ w: at.w, h: at.h, d }) };
}

/** The insert a wood hood of this SKU takes: `{ widthIn, costEach }`. */
function hoodInsertFor(sku) {
  return HOOD_INSERTS[woodHoodSkuOf(sku)?.w] || null;
}

const LOOK_KEYS = [
  'paintColor',
  'paintFinish',
  'doorStyle',
  'doorDesign',
  'texture',
];
const isDoorBearing = (o) =>
  (o?.type === 'upper-cabinet' || o?.type === 'lower-cabinet') &&
  !o?.category?.filler;

/**
 * The look of a run's cabinets: the finish of its first door-bearing cabinet
 * (`paintColor`, `doorDesign`… — whatever it carries).
 */
function woodHoodLookFrom(objects, runId) {
  const source = (objects || []).find(
    (o) => o?.runId === runId && isDoorBearing(o)
  );
  if (!source) return {};
  return Object.fromEntries(
    LOOK_KEYS.filter((k) => source[k] !== undefined).map((k) => [k, source[k]])
  );
}

/**
 * The finish a wood hood wears: its run's cabinets', read when it is drawn
 * or priced — never a copy on the hood, which a restyle that rewrites only
 * the cabinets (the Design Center's Cabinets card, `style_kitchen`) would
 * leave behind. `{}` for a hood no run's cabinets dress.
 */
function woodHoodLookOf(hood, objects) {
  return hood?.runId ? woodHoodLookFrom(objects, hood.runId) : {};
}

/**
 * The finish an object is DRAWN in: a wood hood's is its run's cabinets'
 * (`woodHoodLookOf`), anything else's its own fields. The hood's own drawing
 * and the trim it carries both read it, so the crown on a wood hood is the
 * colour of the box under it.
 */
function drawnFinishOf(obj, objects) {
  return isWoodHood(obj) ? woodHoodLookOf(obj, objects) : obj;
}

/**
 * The fields that make a hood a wood hood: its SKU, and the size record the
 * pick's resize leaves (`userSizedAxes`) — the SKU's box was never the
 * user's, so the metal hood that replaces it is the builder's to size.
 */
const WOOD_HOOD_FIELDS = Object.freeze(['hoodSku', 'userSizedAxes']);

/** The hood without its wood build — the built metal hood's fields. */
function withoutWoodHood(obj) {
  const rest = { ...obj };
  for (const k of WOOD_HOOD_FIELDS) delete rest[k];
  return rest;
}

module.exports = {
  HOOD_INSERTS,
  WOOD_HOOD_STYLES,
  CURVED_WOOD_HOOD,
  woodHoodSkuOf,
  woodHoodShapeOf,
  boxHoodApronM,
  woodHoodInsertRiseM,
  BOX_HOOD_LEDGE_PROUD_IN,
  woodHoodSideProudM,
  WOOD_HOOD_FIELDS,
  withoutWoodHood,
  woodHoodLookOf,
  drawnFinishOf,
  isWoodHood,
  woodHoodUnits,
  woodHoodTiles,
  woodHoodTileId,
  woodHoodTileOf,
  hoodInsertFor,
  woodHoodLookFrom,
};
